import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { ATTRACT_TIMING } from '../data/attract';

const safePositions = [
  [50, 82], [22, 70], [78, 68], [28, 30], [72, 32], [50, 52], [15, 48], [85, 48],
] as const;

export function AttractPressStart({ enabled, onStart, paused, playbackRate }: { enabled: boolean; onStart: () => void; paused: boolean; playbackRate: number }) {
  const [visible, setVisible] = useState(false);
  const [positionIndex, setPositionIndex] = useState(0);
  const elapsedRef = useRef(0);
  const lastTickRef = useRef(performance.now());
  const dodgeTimerRef = useRef<number | null>(null);
  const dodgeCountRef = useRef(0);

  const cancelDodge = () => {
    if (dodgeTimerRef.current !== null) window.clearTimeout(dodgeTimerRef.current);
    dodgeTimerRef.current = null;
  };

  const scheduleDodge = () => {
    if (!visible || dodgeTimerRef.current !== null) return;
    dodgeTimerRef.current = window.setTimeout(() => {
      dodgeTimerRef.current = null;
      dodgeCountRef.current += 1;
      setPositionIndex((current) => (current + 2 + dodgeCountRef.current % 3) % safePositions.length);
    }, 500);
  };

  useEffect(() => {
    let frame: number | null = null;
    const tick = (now: number) => {
      if (!paused) elapsedRef.current += Math.max(0, now - lastTickRef.current) * playbackRate;
      lastTickRef.current = now;
      const promptElapsed = elapsedRef.current - ATTRACT_TIMING.pressStartDelayMs;
      const nextVisible = promptElapsed >= 0 && promptElapsed % ATTRACT_TIMING.pressStartIntervalMs < ATTRACT_TIMING.pressStartVisibleMs;
      setVisible((current) => current === nextVisible ? current : nextVisible);
      frame = requestAnimationFrame(tick);
    };
    const onVisibilityChange = () => {
      if (document.hidden && frame !== null) cancelAnimationFrame(frame);
      if (!document.hidden) {
        lastTickRef.current = performance.now();
        frame = requestAnimationFrame(tick);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    lastTickRef.current = performance.now();
    frame = requestAnimationFrame(tick);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [paused, playbackRate]);

  useEffect(() => {
    if (!visible) cancelDodge();
    return cancelDodge;
  }, [visible]);

  const [x, y] = safePositions[positionIndex];
  const shared = { className: `attract-press-start${visible ? ' is-visible' : ''}${enabled ? ' is-ready' : ''}`, style: { '--press-x': `${x}%`, '--press-y': `${y}%` } as CSSProperties };
  if (enabled) return <button {...shared} type="button" onClick={onStart}>PRESS START</button>;
  return <p {...shared} onPointerEnter={scheduleDodge} onPointerMove={scheduleDodge} onPointerLeave={cancelDodge} onMouseEnter={scheduleDodge} onMouseMove={scheduleDodge} onMouseLeave={cancelDodge} aria-hidden="true">PRESS START</p>;
}
